'use strict';

export default angular
  .module('maestro.services.permissions', [])
  .service('PermissionsService', PermissionsService)
  .name;

function PermissionsService(utils) {

  const CURRENT_ADMIN_LOCAL_STORAGE_KEY = 'App-Admin';

  angular.extend(this, {
    checkPermissions: checkPermissions,

    canIUseMaestro: canIUseMaestro,
    canIViewOrganizationList: canIViewOrganizationList,
    canICreateOrganization: canICreateOrganization,
    canIViewOrganization: canIViewOrganization,
    canIEditOrganization: canIEditOrganization,
    canIDeleteOrganization: canIDeleteOrganization,
    canIManageOrganizationAdmins: canIManageOrganizationAdmins,
    canIViewOrganizationSettings: canIViewOrganizationSettings,
    canIEditOrganizationSettings: canIEditOrganizationSettings,

    canIViewDashboard: canIViewDashboard,

    canIViewUserList: canIViewUserList,
    canIEditUser: canIEditUser,
    canIAccessUserData: canIAccessUserData,
    canIDeleteAndMergeUser: canIDeleteAndMergeUser,
    canICreateUser: canICreateUser,
    canICreateUserAndLocation: canICreateUserAndLocation,
    canICreateAdminUserInOrganization: canICreateAdminUserInOrganization,

    canIViewDeviceList: canIViewDeviceList,
    canICreateDevice: canICreateDevice,
    canIEditDevice: canIEditDevice,
    canIDeleteDevice: canIDeleteDevice,

    canIViewLocationList: canIViewLocationList,
    canICreateLocation: canICreateLocation,
    canIEditLocation: canIEditLocation,
    canIDeleteLocation: canIDeleteLocation,

    canIChangeLocationTestFlag: canIChangeLocationTestFlag,
    canIChangeLocationScene: canIChangeLocationScene,

    canIAddSubscription: canIAddSubscription,
    canIUpgradeSubscription: canIUpgradeSubscription,
    canIDeleteSubscription: canIDeleteSubscription,

    canIViewNotificationList: canIViewNotificationList,
    canIEditNotification: canIEditNotification,
    canIDeleteNotification: canIDeleteNotification,

    canIViewGroupList: canIViewGroupList,

    canIUseGlobalReports: canIUseGlobalReports,
    canIViewReportList: canIViewReportList,
    canIGenerateReport: canIGenerateReport,
    canIUseGlobalSystemStatus: canIUseGlobalSystemStatus,

    canIViewChallengeList: canIViewChallengeList,
    canICreateChallenge: canICreateChallenge,
    canIEditChallenge: canIEditChallenge,
    canIDeleteChallenge: canIDeleteChallenge,

    canIViewMessageList: canIViewMessageList,
    canICreateMessage: canICreateMessage,
    canIEditMessage: canIEditMessage,
    canIDeleteMessage: canIDeleteMessage,
    canIEditMessageTemplates: canIEditMessageTemplates,

    canIManageBots: canIManageBots,
    canIViewLogs: canIViewLogs,

    canIViewCallCenterPasscode: canIViewCallCenterPasscode,

    canIViewBills: canIViewBills,
    canIGenerateBills: canIGenerateBills,
    canIConfigureBilling: canIConfigureBilling,
    canISetupBilling: canISetupBilling,

    isUnlimitedAdmin: isUnlimitedAdmin,
    isSuperAdmin: isSuperAdmin,
    isReportOnlyAdmin: isReportOnlyAdmin,
    isOrgAdmin: isOrgAdmin,
    isPaidAdmin: isPaidAdmin,
    isServiceAdmin: isServiceAdmin,
  });

  // #region Common

  /**
   * Get current user
   * @returns {Object}
   */
  function getCurrentUserInfo() {
    return utils.getStorageItem(CURRENT_ADMIN_LOCAL_STORAGE_KEY);
  }

  /**
   * Get current user permissions
   * @returns {number[]}
   */
  function getCurrentUserPermissions() {
    let userInfo = getCurrentUserInfo();
    if (!userInfo || !userInfo.user || !userInfo.user.permission || !userInfo.user.permission.length) {
      return [];
    }
    return userInfo.user.permission;
  }

  /**
   * Check if current user has permissions.
   * @param {...number} permissions
   * @returns {boolean}
   */
  function checkPermissions(...permissions) {
    if (!permissions || !permissions.length) {
      return true;
    }
    let userPermissions = getCurrentUserPermissions();
    return permissions.every(p => userPermissions.some(up => up.toString() === p.toString()));
  }

  // #endregion

  /**
   * Can current user use Maestro
   */
  function canIUseMaestro() {
    // Do not forget to check if getOrganizations call returns at least one organization
    return checkPermissions(3) || checkPermissions(0) || checkPermissions(2);
  }

  function isUnlimitedAdmin() {
    return checkPermissions(0);
  }

  function isSuperAdmin() {
    return checkPermissions(1);
  }

  function isReportOnlyAdmin() {
    return checkPermissions(2);
  }

  function isOrgAdmin() {
    return checkPermissions(3);
  }

  function isPaidAdmin() {
    return checkPermissions(5);
  }

  function isServiceAdmin() {
    return checkPermissions(8);
  }

  // #region Organization permissions

  function canIViewOrganizationList() {
    return checkPermissions(3) || checkPermissions(0);
  }

  function canICreateOrganization() {
    return checkPermissions(0, 4) || checkPermissions(3, 4);
  }

  /**
   * Test if allowed to view organization
   * @param {*} organization // Selected organization
   * @returns {boolean}
   */
  function canIViewOrganization(organization) {
    return !!organization && (checkPermissions(0) || organization.organizationEditor || organization.organizationAdmin || organization.parentAdmin);
  }

  /**
   * Test if allowed to edit organization
   * @param {*} organization // Selected organization
   * @returns {boolean}
   */
  function canIEditOrganization(organization) {
    let result = checkPermissions(0, 4) || checkPermissions(3, 4);
    if (result && !!organization) {
      result = !!organization.organizationEditor;
    }
    return result;
  }

  function canIDeleteOrganization(organization) {
    return canIEditOrganization(organization);
  }

  function canIManageOrganizationAdmins() {
    return checkPermissions(3, 4, 1);
  }

  function canIViewOrganizationSettings() {
    return checkPermissions(3, 4);
  }

  function canIEditOrganizationSettings() {
    return checkPermissions(0, 4);
  }

  // #endregion

  // #region Dashboard permissions

  function canIViewDashboard() {
    return checkPermissions(3);
  }

  // #endregion

  // #region User permissions

  function canIViewUserList() {
    return checkPermissions(3);
  }

  function canIEditUser(user) {
    if (!checkPermissions(3)) { // minimum permission to edit other users
      return false;
    }
    if (user && (user.role || user.roleId)) { // user is an admin
      // We can't edit other admins that has non-grantable permissions
      if (user.permission && user.permission.length && user.permission.some(p => p === 0 || p === 2 || p === 5 || p === 6 || p === 7)) {
        return checkPermissions(0); // unlimited access exception
      }
      return checkPermissions(1); // We can edit other admins only with permission 1
    }
    return true;
  }

  function canIAccessUserData(locations) {
    if (checkPermissions(0, 3)) {
      return true;
    }
    if (locations && locations.length > 0) {
      return locations.every(l => canIEditLocation(l));
    }
    return false;
  }

  function canIDeleteAndMergeUser(user) {
    if (!checkPermissions(3, 8)) {
      return false;
    }
    // We can't edit other admins
    if (user && user.permission && user.permission.length) {
      if (!checkPermissions(1)) {
        return false;
      } else {
        // Do not allow to edit other unlimited or power admins
        if (user.permission.some(p => p === 0 || p === 5)) {
          return false;
        }
      }
    }
    return true;
  }

  function canICreateUser() {
    return checkPermissions(3);
  }

  function canICreateUserAndLocation() {
    return canICreateUser() && canICreateLocation();
  }

  function canICreateAdminUserInOrganization() {
    return canICreateUser() && canIManageOrganizationAdmins();
  }

  // #endregion

  // #region Device permissions

  function canIViewDeviceList() {
    return checkPermissions(3);
  }

  function canICreateDevice() {
    return checkPermissions(3);
  }

  function canIEditDevice(device) {
    return checkPermissions(3);
  }

  function canIDeleteDevice(device) {
    return checkPermissions(3);
  }

  // #endregion

  // #region Location permissions

  function canIViewLocationList() {
    return checkPermissions(3);
  }

  function canICreateLocation() {
    return checkPermissions(3);
  }

  function canIEditLocation(location) {
    if (location && !location.organizationId) {
      return false;
    }
    return checkPermissions(3);
  }

  function canIDeleteLocation(location) {
    if (location && !location.organizationId) {
      return false;
    }
    return checkPermissions(3);
  }

  function canIChangeLocationTestFlag(location) {
    return checkPermissions(0);
  }

  function canIChangeLocationScene(location) {
    return checkPermissions(8);
  }

  function canIViewCallCenterPasscode() {
    return checkPermissions(8);
  }

  // #endregion

  // #region Subscriptions permissions

  /**
   * Add specific Subscriptions to location
   * @param subscription
   * @return {boolean}
   */
  function canIAddSubscription(subscription) {
    if (!subscription) {
      return false;
    }
    if (subscription.appName) { // appName - generic flag, we set it manually for "paid" subscriptions
      return checkPermissions(3, 5);
    } else {
      return checkPermissions(3);
    }
  }

  /* Upgrade Subscription */
  function canIUpgradeSubscription() {
    return checkPermissions(3, 5); // You should check "upgradableTo" field by yourself!
  }

  function canIDeleteSubscription(subscription) {
    if (!subscription || !subscription.plan) {
      return false;
    }
    // appName - generic flag, we set it for "paid" branded subscriptions
    if (subscription.plan.appName) {
      return checkPermissions(3, 5);
    } else {
      return checkPermissions(3);
    }
  }

  // #endregion

  // #region Notification permissions

  function canIViewNotificationList() {
    return checkPermissions(3);
  }

  function canIEditNotification(notification) {
    return checkPermissions(3);
  }

  function canIDeleteNotification(notification) {
    return checkPermissions(3);
  }

  // #endregion

  // #region Grouping tags permissions

  function canIViewGroupList() {
    return checkPermissions(3);
  }

  // #endregion

  // #region Report permissions

  function canIUseGlobalReports() {
    return checkPermissions(2);
  }

  function canIViewReportList() {
    return checkPermissions(3) || checkPermissions(0, 2) || checkPermissions(2);
  }

  function canIGenerateReport(report) {
    return checkPermissions(3) || checkPermissions(0, 2) || checkPermissions(2);
  }

  function canIUseGlobalSystemStatus() {
    return checkPermissions(0, 2);
  }

  // #endregion

  // #region Challenge permissions

  function canIViewChallengeList() {
    return checkPermissions(3);
  }

  function canICreateChallenge() {
    return checkPermissions(3);
  }

  function canIEditChallenge() {
    return checkPermissions(3);
  }

  function canIDeleteChallenge() {
    return checkPermissions(3);
  }

  // #endregion

  // #region Message permissions

  function canIViewMessageList() {
    return checkPermissions(3);
  }

  function canICreateMessage() {
    return checkPermissions(3);
  }

  function canIEditMessage(message) {
    return checkPermissions(3);
  }

  function canIDeleteMessage(message) {
    return checkPermissions(3);
  }

  function canIEditMessageTemplates() {
    return checkPermissions(3);
  }

  // #endregion

  // #region Bots permissions

  function canIManageBots() {
    return checkPermissions(3);
  }

  function canIViewLogs() {
    return checkPermissions(0) || checkPermissions(6);
  }

  // #endregion

  // #region Billing permissions

  /**
   * Test if allowed to view and download organization bills
   * @param {*} organization // Selected organization
   * @returns {boolean}
   */
  function canIViewBills(organization) {
    return canIEditOrganization(organization);
  }

  /**
   * Test if allowed to generate bills
   * @param {*} organization // Selected organization
   * @returns {boolean}
   */
  function canIGenerateBills(organization) {
    return checkPermissions(11) && canIEditOrganization(organization);
  }

  /**
   * Test if allowed to configure billing
   * @returns {boolean}
   */
  function canIConfigureBilling() {
    return checkPermissions(11);
  }

  /**
   * Test if allowed to setup organization billing
   * @param {*} organization // Selected organization
   * @returns {boolean}
   */
  function canISetupBilling(organization) {
    return checkPermissions(11) && canIEditOrganization(organization);
  }

  // #endregion

}
